import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Sparkles, Check, AlertCircle, CreditCard, Calendar, Rocket, X,
} from 'lucide-react'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { EmptyState } from '../../components/ui/EmptyState'
import { useAbbonamento } from '../../hooks/useAbbonamento'
import { useToast } from '../../stores/toastStore'
import { messaggioErrore } from '../../lib/api'
import { formatData } from '../../lib/format'
import pagina from '../pagine.module.css'
import styles from './Abbonamento.module.css'

/**
 * Pagina di gestione dell'abbonamento.
 *
 * UI condizionata da `stato.stato`:
 *  - 'nessuno'    → griglia piani (Free attuale + CTA Pro/Team)
 *  - 'trial'      → countdown + bottoni gestisci/cancella
 *  - 'attivo'     → conferma + bottone portale Stripe
 *  - 'cancellato' → avviso fine periodo + bottone riattiva
 *  - 'scaduto'    → trattato come 'nessuno' (mostra griglia)
 */
export function Abbonamento() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const toast = useToast()
  const { stato, isLoading, refetch, avviaCheckout, apriPortale, cancellaTrial } = useAbbonamento()
  const [modaleCancella, setModaleCancella] = useState(false)
  const [pianoInLoading, setPianoInLoading] = useState<'pro' | 'team' | null>(null)

  // Ritorno da Stripe Checkout: gestisci ?stato=successo|annullato
  useEffect(() => {
    const ritorno = params.get('stato')
    const pianoSel = params.get('piano')
    if (ritorno === 'successo' && pianoSel) {
      toast.successo(`🎉 Trial ${pianoSel.toUpperCase()} avviato! Hai 7 giorni gratuiti.`)
      void refetch()
      navigate('/app/abbonamento', { replace: true })
    } else if (ritorno === 'annullato') {
      toast.info('Pagamento annullato.')
      navigate('/app/abbonamento', { replace: true })
    }
  // Eseguire solo al mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isLoading) return <div className={pagina.skeleton}>Caricamento abbonamento…</div>
  if (!stato) return <EmptyState titolo="Impossibile caricare lo stato abbonamento" />

  const onCheckout = async (piano: 'pro' | 'team') => {
    setPianoInLoading(piano)
    try {
      const { checkout_url } = await avviaCheckout.mutateAsync(piano)
      window.location.href = checkout_url
    } catch (err) {
      toast.errore(messaggioErrore(err))
      setPianoInLoading(null)
    }
  }

  const onPortale = async () => {
    try {
      const { portale_url } = await apriPortale.mutateAsync()
      window.location.href = portale_url
    } catch (err) {
      toast.errore(messaggioErrore(err))
    }
  }

  const onCancellaTrial = async () => {
    try {
      await cancellaTrial.mutateAsync()
      toast.successo('Trial cancellato. Sei tornato al piano Free.')
      setModaleCancella(false)
    } catch (err) {
      toast.errore(messaggioErrore(err))
    }
  }

  return (
    <div className={pagina.wrap}>
      <div className={pagina.header}>
        <div>
          <h1 className={pagina.titolo}>Il tuo piano</h1>
          <div className={pagina.sottotitolo}>Gestisci il tuo abbonamento RingCoach.</div>
        </div>
      </div>

      {/* ── Stato: TRIAL ───────────────────────────────────────── */}
      {stato.stato === 'trial' && (
        <Card>
          <div className={styles.trialBox}>
            <div className={styles.trialIcona}>🎉</div>
            <div className={styles.trialTitolo}>
              Stai usando RingCoach {stato.piano.toUpperCase()} — <strong>Trial</strong>
            </div>

            <div className={styles.countdownWrap}>
              <Calendar size={16} />
              <span>
                <strong>{stato.giorni_trial_rimanenti ?? 0} giorni rimanenti</strong> al trial gratuito
              </span>
            </div>
            <BarraTrial giorniRimanenti={stato.giorni_trial_rimanenti ?? 0} />

            <p className={styles.trialNota}>
              Alla fine del trial il piano si rinnova automaticamente al prezzo del piano <strong>{stato.piano}</strong>.
              {stato.trial_fine && <> Fine trial: {formatData(stato.trial_fine)}.</>}
            </p>

            <div className={styles.azioni}>
              <Button
                variante="secondary"
                iconaSinistra={<CreditCard size={14} />}
                onClick={onPortale}
                caricamento={apriPortale.isPending}
              >
                Gestisci abbonamento
              </Button>
              <Button
                variante="ghost"
                onClick={() => setModaleCancella(true)}
              >
                Cancella trial
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* ── Stato: ATTIVO ──────────────────────────────────────── */}
      {stato.stato === 'attivo' && (
        <Card>
          <div className={styles.attivoBox}>
            <div className={styles.iconaCheck}>
              <Check size={28} />
            </div>
            <h2>RingCoach {stato.piano.toUpperCase()} — Attivo</h2>
            <p className={styles.attivoSottotitolo}>
              Il tuo abbonamento è in corso. Gestisci pagamento, fatture o downgrade dal portale Stripe.
            </p>
            <div className={styles.azioni}>
              <Button
                iconaSinistra={<CreditCard size={14} />}
                onClick={onPortale}
                caricamento={apriPortale.isPending}
              >
                Gestisci abbonamento
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* ── Stato: CANCELLATO (accesso fino a scadenza) ────────── */}
      {stato.stato === 'cancellato' && (
        <Card>
          <div className={styles.cancellatoBox}>
            <div className={styles.iconaWarning}>
              <AlertCircle size={28} />
            </div>
            <h2>Abbonamento in cancellazione</h2>
            <p className={styles.cancellatoTesto}>
              Hai ancora accesso al piano <strong>{stato.piano.toUpperCase()}</strong>
              {stato.abbonamento_fine && <> fino al <strong>{formatData(stato.abbonamento_fine)}</strong></>}.
              Dopo quella data tornerai automaticamente al piano Free.
            </p>
            <div className={styles.azioni}>
              <Button
                iconaSinistra={<Rocket size={14} />}
                onClick={onPortale}
                caricamento={apriPortale.isPending}
              >
                Riattiva abbonamento
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* ── Stato: NESSUNO / SCADUTO → mostra griglia piani ──── */}
      {(stato.stato === 'nessuno' || stato.stato === 'scaduto') && (
        <div className={styles.grigliaPiani}>
          {/* FREE — attuale per chi non ha mai sottoscritto */}
          <CardPiano
            nome="FREE"
            prezzo="€0"
            cadenza="/mese"
            features={[
              '3 sessioni al mese',
              'Solo lead pubblici',
              'Feedback AI base',
            ]}
            attuale={stato.piano === 'free'}
          />

          {/* PRO */}
          <CardPiano
            nome="PRO"
            prezzo="€49"
            cadenza="/mese"
            consigliato
            features={[
              'Sessioni illimitate',
              '10 lead personalizzati',
              'Feedback AI avanzato',
              'Voce ElevenLabs personalizzata',
              'Export report PDF',
            ]}
            attuale={stato.piano === 'pro'}
            azione={
              stato.piano !== 'pro' && (
                <Button
                  iconaSinistra={<Sparkles size={14} />}
                  taglia="lg"
                  caricamento={pianoInLoading === 'pro'}
                  onClick={() => onCheckout('pro')}
                >
                  Prova gratis 7 giorni
                </Button>
              )
            }
          />

          {/* TEAM */}
          <CardPiano
            nome="TEAM"
            prezzo="€149"
            cadenza="/mese"
            features={[
              'Fino a 5 SDR',
              'Dashboard manager',
              'Lead condivisi nel team',
              'Tutti i benefit del Pro',
            ]}
            attuale={stato.piano === 'team'}
            azione={
              stato.piano !== 'team' && (
                <Button
                  variante="secondary"
                  iconaSinistra={<Sparkles size={14} />}
                  taglia="lg"
                  caricamento={pianoInLoading === 'team'}
                  onClick={() => onCheckout('team')}
                >
                  Prova gratis 7 giorni
                </Button>
              )
            }
          />
        </div>
      )}

      {/* Modale conferma cancellazione trial */}
      <Modal
        aperto={modaleCancella}
        titolo="Cancellare il trial?"
        onChiudi={() => setModaleCancella(false)}
      >
        <p style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
          Tornerai <strong>immediatamente</strong> al piano Free. Perderai sessioni illimitate
          e accesso ai lead personalizzati.
          Sei sicuro?
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variante="ghost" onClick={() => setModaleCancella(false)}>Mantieni trial</Button>
          <Button
            variante="danger"
            iconaSinistra={<X size={14} />}
            caricamento={cancellaTrial.isPending}
            onClick={onCancellaTrial}
          >
            Cancella trial
          </Button>
        </div>
      </Modal>
    </div>
  )
}

// ─── Sotto-componenti ────────────────────────────────────────────

interface CardPianoProps {
  nome: string
  prezzo: string
  cadenza: string
  features: string[]
  attuale?: boolean
  consigliato?: boolean
  azione?: React.ReactNode
}

function CardPiano({ nome, prezzo, cadenza, features, attuale, consigliato, azione }: CardPianoProps) {
  return (
    <div className={`${styles.cardPiano} ${consigliato ? styles.cardConsigliato : ''}`}>
      {consigliato && <div className={styles.consigliatoBadge}>★ Consigliato</div>}
      <div className={styles.pianoNome}>{nome}</div>
      {attuale && <Badge variante="accent">Piano attuale</Badge>}
      <div className={styles.prezzo}>
        <span className={styles.prezzoCifra}>{prezzo}</span>
        <span className={styles.prezzoCadenza}>{cadenza}</span>
      </div>
      <ul className={styles.features}>
        {features.map((f) => (
          <li key={f}>
            <Check size={14} color="var(--success)" /> {f}
          </li>
        ))}
      </ul>
      {azione && <div className={styles.pianoAzione}>{azione}</div>}
    </div>
  )
}

function BarraTrial({ giorniRimanenti }: { giorniRimanenti: number }) {
  // 7 giorni totali. Pieno = 7gg rimanenti, vuoto = 0.
  const pct = Math.max(0, Math.min(100, (giorniRimanenti / 7) * 100))
  return (
    <div className={styles.barraTrialTrack}>
      <div className={styles.barraTrialFill} style={{ width: `${pct}%` }} />
    </div>
  )
}
